import React, { FunctionComponent, ReactNode, MouseEvent } from 'react'
import cx from 'classnames'
import parse, { domToReact, Element as DomElement, HTMLReactParserOptions } from 'html-react-parser' // eslint-disable-line no-unused-vars
import { isString } from 'libraries/lodash'
import { updateHistoryState } from 'libraries/front-end-core/core/history'
import { CURRENT_AWARENESS } from 'constants/controllers'
import { dismissPopover } from 'services/OverlayService'
import { styleStringToObject } from 'helpers/parseHtmlContent'
import HoverIconButtonContainer from 'redux/containers/components/accordion/HoverIconButtonContainer'
import { ContentHeaderLabelContainerProps } from 'redux/containers/components/accordion/ContentHeaderLabelContainer'
import { ContentHeaderProps } from './ContentHeader'

const ContentHeaderLabel: FunctionComponent<ContentHeaderLabelProps> = ({
  aol,
  id,
  nodeId,
  depth,
  atBottom,
  expanded,
  prefix,
  title,
  isLogged,
  children,
  eventCategory,
  setAccordionUrlNode,
  controller,
  setSignUpModalIsOpen,
  hasAccess,
  setUserRolesModalIsOpen
}: ContentHeaderLabelProps) => {
  const handleOnClick = (
    e: MouseEvent<HTMLLabelElement>
  ): void => {
    if (!(expanded as boolean) && hasAccess !== true) {
      dismissPopover(e.target)
      e.preventDefault()
      e.stopPropagation()

      if (isLogged) {
        setUserRolesModalIsOpen(true)
      } else {
        setSignUpModalIsOpen(true)
      }
    } else {
      const urlNode = nodeId
      if (controller !== CURRENT_AWARENESS) {
        if (urlNode !== undefined) setAccordionUrlNode(urlNode)
        history.pushState({}, '', urlNode ?? '')
        updateHistoryState({ viewName: controller })
      }
    }
  }

  return (
    <label
      onClick={handleOnClick}
      className={cx('min-h3 w-100 ma0 f5 normal flex items-center lh-title hover-underline shadow-none',
        `depth-${eventCategory !== 'CA' ? 1 : (depth ?? 1)}`,
        { underline: (expanded as boolean) }
      )}
      htmlFor={`${id ?? ''}/${atBottom ?? 0}`}
      data-event-category='Accordion'
      data-event-action={(expanded as boolean) ? 'Collapse' : 'Expand'}
      data-event-label={`[Content ${prefix ?? ''}] ${(title ?? '').replace(/(<([^>]+)>)/gi, '')}`}
      {...(aol !== undefined && {
        'data-event-aol': Array.isArray(aol) ? aol.join(',') : aol
      })}
    >
      {children}
      <div className='flex items-center'>
        {isString(title) && depth !== undefined && <Title {...{ title, depth: eventCategory === 'CA' ? 1 : depth, eventCategory }} />}
        {nodeId !== undefined && <HoverIconButtonContainer {...{ nodeId }} />}
      </div>
    </label>
  )
}

const Title: FunctionComponent<TitleProps> = ({ title, depth }) => {
  const contentDepthStyle = (depth: number): { marginLeft: string } => ({ marginLeft: `${depth > 1 ? depth + 2 : 2}.75rem` })
  const trimmedTitle = title.replace(/style *= *"[^"]*"/g, '') // have to do exclude " instead of any char to stop the regex being too greedy.
  const cleanedTrimmedTitle = parseHtmlContent(trimmedTitle)

  return (
    <span
      style={contentDepthStyle(depth)}
      className='b mv5 mr9'
    >
      {cleanedTrimmedTitle}
    </span>
  )
}

const options: HTMLReactParserOptions = {
  replace: (domNode: DomElement) => {
    const { attribs, name, children } = domNode
    const styleObj = attribs !== undefined ? styleStringToObject(attribs.style) : undefined

    switch (name) {
      case 'p':
        return (
          React.createElement('p', { style: { ...styleObj, marginTop: 0, marginBottom: 0 } }, domToReact(children))
        )
      default:
        return null
    }
  }
}

const parseHtmlContent = (dirtyHtml: string): ReturnType<typeof parse> => parse(dirtyHtml, options)

interface TitleProps {
  title: string
  depth: number
}

type ParentProps = ContentHeaderLabelContainerProps & Partial<ContentHeaderProps>

interface ContentHeaderLabelProps extends ParentProps {
  aol?: string | string[]
  children: ReactNode
}

export default ContentHeaderLabel
